/**
 * Collection Description Read More/Less Component
 * Handles expanding and collapsing collection descriptions that exceed 200 characters
 */

class CollectionDescriptionReadMore extends HTMLElement {
  constructor() {
    super();
    this.expanded = false;
    this.maxLength = 200;
    this.isAnimating = false;
    this.animationDuration = 300;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    const content = /** @type {HTMLElement} */ (this.querySelector('.collection-description-content'));
    const button = /** @type {HTMLElement} */ (this.querySelector('.collection-description-toggle'));
    
    if (!content || !button) return;

    const fullText = content.dataset.fullText || content.textContent.trim();
    const plainText = this.stripHtml(fullText);

    // Only show button if text is longer than maxLength
    if (plainText.length <= this.maxLength) {
      button.style.display = 'none';
      return;
    }

    // Set full text
    content.dataset.fullText = fullText;
    
    // Set initial truncated text
    this.updateContent(false, false);

    // Add click handler
    button.addEventListener('click', (e) => {
      e.preventDefault();
      if (!this.isAnimating) {
        this.toggle();
      }
    });
  }

  /**
   * @param {string} html
   * @returns {string}
   */
  stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  /**
   * @param {string} text
   * @param {number} maxLength
   * @returns {string}
   */
  truncateText(text, maxLength) {
    const plainText = this.stripHtml(text);
    if (plainText.length <= maxLength) {
      return text;
    }

    // Find a good breaking point (end of word)
    let truncated = plainText.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    if (lastSpace > maxLength * 0.8) {
      truncated = truncated.substring(0, lastSpace);
    }

    // For HTML content, we'll create a simple truncated version
    // by wrapping the truncated plain text in a span
    // The full HTML will be shown when expanded
    return '<span>' + this.escapeHtml(truncated) + '...</span>';
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * @param {boolean} isExpanded
   * @param {boolean} animate
   */
  updateContent(isExpanded, animate = true) {
    const content = /** @type {HTMLElement} */ (this.querySelector('.collection-description-content'));
    const button = /** @type {HTMLElement} */ (this.querySelector('.collection-description-toggle'));
    
    if (!content || !button) return;

    const fullText = content.dataset.fullText || '';
    
    if (animate && !this.isAnimating) {
      this.animateContent(isExpanded, content, button, fullText);
    } else {
      // Direct update without animation
      if (isExpanded) {
        content.innerHTML = fullText;
        button.textContent = button.dataset.lessText || 'Read Less';
        this.expanded = true;
      } else {
        const truncated = this.truncateText(fullText, this.maxLength);
        content.innerHTML = truncated;
        button.textContent = button.dataset.moreText || 'Read More';
        this.expanded = false;
      }
    }
  }

  /**
   * @param {boolean} toExpanded
   * @param {HTMLElement} content
   * @param {HTMLElement} button
   * @param {string} fullText
   */
  animateContent(toExpanded, content, button, fullText) {
    if (this.isAnimating) return;
    
    this.isAnimating = true;
    
    // Get current height before changing content
    const startHeight = content.scrollHeight;
    
    // Prepare new content
    const newContent = toExpanded ? fullText : this.truncateText(fullText, this.maxLength);
    
    // Update button text immediately for better UX
    if (toExpanded) {
      button.textContent = button.dataset.lessText || 'Read Less';
    } else {
      button.textContent = button.dataset.moreText || 'Read More';
    }
    
    // For collapsing, we need to measure the target height first
    // by temporarily updating content and measuring, then restoring
    let endHeight = 0;
    if (!toExpanded) {
      // Save current content
      const currentContent = content.innerHTML;
      const currentHeight = content.style.height;
      const currentOverflow = content.style.overflow;
      
      // Temporarily remove height constraint to measure target
      content.style.height = 'auto';
      content.style.overflow = 'hidden';
      content.innerHTML = newContent;
      
      // Force reflow and measure
      void content.offsetHeight;
      endHeight = content.scrollHeight;
      
      // Restore original content for animation
      content.innerHTML = currentContent;
      content.style.height = currentHeight;
      content.style.overflow = currentOverflow;
      void content.offsetHeight;
    }
    
    // Set up overflow hidden and transition
    content.style.overflow = 'hidden';
    content.style.transition = `height ${this.animationDuration}ms ease-in-out`;
    
    // Lock to current height
    content.style.height = startHeight + 'px';
    
    // Force reflow to ensure styles are applied
    void content.offsetHeight;
    
    // Update content while height is locked
    content.innerHTML = newContent;
    
    // For expanding, measure target height after content update
    if (toExpanded) {
      // Force reflow to ensure new content is rendered
      void content.offsetHeight;
      endHeight = content.scrollHeight;
    }
    
    // If heights are the same, skip animation
    if (Math.abs(startHeight - endHeight) < 1) {
      content.style.height = '';
      content.style.overflow = '';
      content.style.transition = '';
      this.expanded = toExpanded;
      this.isAnimating = false;
      return;
    }
    
    // Use requestAnimationFrame to ensure the browser has processed
    // the height lock and transition setup before we start animating
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Now animate to target height
        // The transition will handle the smooth animation
        content.style.height = endHeight + 'px';
      });
    });
    
    // Clean up after animation completes
    const cleanup = () => {
      content.style.height = '';
      content.style.overflow = '';
      content.style.transition = '';
      this.expanded = toExpanded;
      this.isAnimating = false;
    };
    
    setTimeout(cleanup, this.animationDuration);
  }

  toggle() {
    this.updateContent(!this.expanded, true);
  }
}

if (!customElements.get('collection-description-readmore')) {
  customElements.define('collection-description-readmore', CollectionDescriptionReadMore);
}

